import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Security,
  Cloud,
  Storage,
  Settings,
  Dashboard,
  Code
} from '@mui/icons-material';
import Shape from '../common/Shape';
import theme from '../../theme/theme';

function Services() {
  const services = [
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Website Design",
      description:
        "We design modern, clean & responsive websites that look great on any device.",
      features: ["Custom Designs", "Responsive Layouts", "User Experience Focused"],
    },
    {
      icon: <Cloud sx={{ fontSize: 40 }} />,
      title: "Frontend & Backend Development",
      description:
        "We handle the full development process — frontend design & backend logic that works perfectly together.",
      features: [
        "Clean & maintainable code",
        "Secure databases & APIs",
        "Modern Optimization",
      ],
    },
    {
      icon: <Storage sx={{ fontSize: 40 }} />,
      title: "Maintenance & Support",
      description:
        "Comprehensive maintenance and support services to ensure your systems run smoothly.",
      features: ["Regular Updates", "Performance Monitoring", "Technical Support"],
    },
    {
      icon: <Settings sx={{ fontSize: 40 }} />,
      title: "E-commerce Solutions",
      description:
        "We build easy-to-manage online stores to help you sell your products or services smoothly.",
      features: [
        "Secure payment integration",
        "Inventory Management",
        "User-friendly interfaces",
      ],
    },
    {
      icon: <Dashboard sx={{ fontSize: 40 }} />,
      title: "Branding & UI Design",
      description:
        "We help you build a strong brand identity with clean UI designs & appealing visuals.",
      features: ["Brand Strategy", "Visual Identity", "User Experience Design"],
    },
    {
      icon: <Code sx={{ fontSize: 40 }} />,
      title: "Software Development",
      description:
        "Custom software solutions designed to solve your unique business challenges.",
      features: ["Web Applications", "Mobile Apps", "Enterprise Software"],
    },
  ];

  return (
    <Box
      id="services"
      sx={{
        py: { xs: 8, md: 10 },
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Background Shapes */}
      <Shape color={theme.palette.secondary.main} size={500} position={{ top: "10%", right: "-5%" }} rotation={0} />
      <Shape color={theme.palette.secondary.main} size={500} position={{ top: "20%", right: "-10%" }} rotation={0} />
      <Shape color={theme.palette.secondary.main} size={400} position={{ bottom: "20%", left: "-5%" }} rotation={60} />
      <Shape color={theme.palette.secondary.main} size={400} position={{ bottom: "30%", left: "-10%" }} rotation={60} />

      <Container maxWidth="xl">
        {/* Heading */}
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 700,
            mx: "auto",
            mb: { xs: 4, md: 8 },
            px: { xs: 2, md: 0 },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontSize: {
                xs: "clamp(1.5rem, 5vw, 1.5rem)",
                sm: "clamp(2.4rem, 6vw, 2.4rem)",
                md: "clamp(3rem, 7vw, 3rem)",
              },
              fontWeight: 700,
            }}
          >
            Our IT{" "}
            <Box component="span" sx={{ color: theme.palette.secondary.main }}>
              Services & Tools
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.primary.light,
              maxWidth: 800,
              mx: "auto",
              mt: 1,
              px: { xs: 2, md: 0 },
              fontSize: {
                xs: "clamp(0.75rem, 3.5vw, 0.75rem)",
                sm: "clamp(1rem, 3.5vw, 1rem)",
                md: "clamp(1.125rem, 3vw, 1.125rem)",
              },
            }}
            component="div"
          >
            Comprehensive IT solutions designed to optimize your business
            operations and drive growth
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: "#ffffff",
                  border: `1px solid ${theme.palette.primary.light}`,
                  mx: { xs: "auto", md: 0 },
                  maxWidth: 360,
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 3, md: 4 },
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      p:1 ,
                      px:1,
                      borderRadius: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      background: theme.palette.primary.light,
                      color: theme.palette.secondary.main,
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontSize: {
                        xs: "clamp(1.1rem, 4vw, 1.1rem)",
                        md: "clamp(1.4rem, 4.5vw, 1.4rem)",
                      },
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      flexGrow: 1,
                      color: theme.palette.text.primary,
                      fontSize: {
                        xs: "clamp(0.75rem, 3vw, 0.75rem)",
                        md: "clamp(1.1rem, 3vw, 1.1rem)",
                      },
                    }}
                    component="div"
                  >
                    {service.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {service.features.map((feature, i) => (
                      <Chip
                        key={i}
                        label={feature}
                        size="small"
                        sx={{
                          mr: 1,
                          mb: 1,
                          background: "rgba(74, 123, 255, 0.1)",
                          color: theme.palette.secondary.main,
                          fontWeight: 500,
                          fontSize: {
                            xs: "clamp(0.7rem, 2.8vw, 0.7rem)",
                            md: "clamp(0.8rem, 2.5vw, 0.8rem)",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}


export default Services;
